import React from 'react';
import { useTranslation } from 'react-i18next';

const TableCell = ({ label, value, colorClass }) => (
  <td className="karma-td">
    <div className="cell-label">{label}</div>
    <div className={`cell-value ${colorClass || ''}`}>
      {value !== null && value !== undefined ? value : '—'}
    </div>
  </td>
);

// Table row definitions in original order
const TABLE_ROWS = [
  {
    id: 0,
    cells: [
      { label: 'А. Gün', mainKey: 'A', addKey: 'A1', mainColor: 'text-orange', addColor: 'text-orange-light' },
      { label: 'Д. NƏ1', mainKey: 'D', addKey: 'D1', mainColor: 'text-blue', addColor: 'text-blue-light' },
      { label: 'И. KD1', mainKey: 'I', addKey: 'I1', mainColor: 'text-cyan', addColor: 'text-cyan-light' },
    ],
  },
  {
    id: 1,
    cells: [
      { label: 'Б. Ay', mainKey: 'B', addKey: 'B1', mainColor: 'text-violet', addColor: 'text-violet-light' },
      { label: 'Е. NƏ2', mainKey: 'E', addKey: 'E1', mainColor: 'text-red', addColor: 'text-red-light' },
      { label: 'К. KD2', mainKey: 'K', addKey: 'K1', mainColor: 'text-orange', addColor: 'text-orange-light' },
    ],
  },
  {
    id: 2,
    cells: [
      { label: 'В. İl', mainKey: 'V', addKey: 'V1', mainColor: 'text-orange', addColor: 'text-orange-light' },
      { label: 'Ж. NƏ3', mainKey: 'Zh', addKey: 'Zh1', mainColor: 'text-blue', addColor: 'text-blue-light' },
      { label: 'Л. KD3', mainKey: 'L', addKey: 'L1', mainColor: 'text-cyan', addColor: 'text-cyan-light' },
    ],
  },
  {
    id: 3,
    cells: [
      { label: 'Г. Yekun', mainKey: 'G', addKey: 'G1', mainColor: 'text-green', addColor: 'text-green-light' },
      { label: 'З. NƏ4', mainKey: 'Z', addKey: 'Z1', mainColor: 'text-blue', addColor: 'text-blue-light' },
      { label: 'М. KD4', mainKey: 'M', addKey: 'M1', mainColor: 'text-yellow', addColor: 'text-yellow-light' },
    ],
  },
];

const KarmaTable = ({ results, rowOrder, specialOnly, nodeLabels, hiddenRows, hiddenColumns, customRows, evaluateFormula }) => {
  const { t } = useTranslation();
  const r = results || {};
  const v = (key) => results ? r[key] : null;
  const activeRowOrder = rowOrder || [0, 1, 2, 3];
  const hidden = new Set((hiddenRows || []).map(Number));
  const hiddenCols = new Set((hiddenColumns || []).map(s => String(s).toLowerCase()));
  const extra = customRows || [];
  // Column groups: index 0=yol (cells[0]), 1=ne (cells[1]), 2=kd (cells[2])
  const colKeys = ['yol', 'ne', 'kd'];
  const isColVisible = (colIdx) => !hiddenCols.has(colKeys[colIdx]);

  // Build dynamic label function
  const getNodeLabel = (nodeId) => {
    if (nodeLabels && nodeLabels[nodeId]) {
      return `${nodeLabels[nodeId].letter}. ${nodeLabels[nodeId].desc}`;
    }
    return null;
  };

  const getNodeLetter = (nodeId) => {
    if (nodeLabels && nodeLabels[nodeId]) {
      return nodeLabels[nodeId].letter;
    }
    return null;
  };

  // Build table rows with dynamic labels
  const buildRows = () => {
    const defaultRows = [
      {
        id: 0,
        cells: [
          { nodeId: 'A', defaultLabel: 'А. Gün', mainKey: 'A', addKey: 'A1', mainColor: 'text-orange', addColor: 'text-orange-light' },
          { nodeId: 'D', defaultLabel: 'Д. NƏ1', mainKey: 'D', addKey: 'D1', mainColor: 'text-blue', addColor: 'text-blue-light' },
          { nodeId: 'I', defaultLabel: 'И. KD1', mainKey: 'I', addKey: 'I1', mainColor: 'text-cyan', addColor: 'text-cyan-light' },
        ],
      },
      {
        id: 1,
        cells: [
          { nodeId: 'B', defaultLabel: 'Б. Ay', mainKey: 'B', addKey: 'B1', mainColor: 'text-violet', addColor: 'text-violet-light' },
          { nodeId: 'E', defaultLabel: 'Е. NƏ2', mainKey: 'E', addKey: 'E1', mainColor: 'text-red', addColor: 'text-red-light' },
          { nodeId: 'K', defaultLabel: 'К. KD2', mainKey: 'K', addKey: 'K1', mainColor: 'text-orange', addColor: 'text-orange-light' },
        ],
      },
      {
        id: 2,
        cells: [
          { nodeId: 'V', defaultLabel: 'В. İl', mainKey: 'V', addKey: 'V1', mainColor: 'text-orange', addColor: 'text-orange-light' },
          { nodeId: 'Zh', defaultLabel: 'Ж. NƏ3', mainKey: 'Zh', addKey: 'Zh1', mainColor: 'text-blue', addColor: 'text-blue-light' },
          { nodeId: 'L', defaultLabel: 'Л. KD3', mainKey: 'L', addKey: 'L1', mainColor: 'text-cyan', addColor: 'text-cyan-light' },
        ],
      },
      {
        id: 3,
        cells: [
          { nodeId: 'G', defaultLabel: 'Г. Yekun', mainKey: 'G', addKey: 'G1', mainColor: 'text-green', addColor: 'text-green-light' },
          { nodeId: 'Z', defaultLabel: 'З. NƏ4', mainKey: 'Z', addKey: 'Z1', mainColor: 'text-blue', addColor: 'text-blue-light' },
          { nodeId: 'M', defaultLabel: 'М. KD4', mainKey: 'M', addKey: 'M1', mainColor: 'text-yellow', addColor: 'text-yellow-light' },
        ],
      },
    ];
    return defaultRows;
  };

  const tableRows = buildRows();

  // If specialOnly, only render HRT/Manipura cards
  if (specialOnly) {
    return (
      <div className="karma-table-wrapper">
        <div className="karma-special-row">
          <div className="karma-special-card hrt-card">
            <div className="special-card-title">HRT</div>
            <div className="special-card-values">
              <div className="special-val">
                <span className="special-label">Əsas</span>
                <span className="special-number hrt-color">{v('HRT') ?? '—'}</span>
              </div>
              <div className="special-divider" />
              <div className="special-val">
                <span className="special-label">Əlavə</span>
                <span className="special-number hrt-color-light">{v('HRT1') ?? '—'}</span>
              </div>
            </div>
          </div>

          <div className="karma-special-card manipura-card">
            <div className="special-card-title">Manipura</div>
            <div className="special-card-values">
              <div className="special-val">
                <span className="special-label">Əsas</span>
                <span className="special-number manipura-color">{v('Man') ?? '—'}</span>
              </div>
              <div className="special-divider" />
              <div className="special-val">
                <span className="special-label">Əlavə</span>
                <span className="special-number manipura-color-light">{v('Man1') ?? '—'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render main table with dynamic row order, filter hidden rows, append custom rows
  const orderedRows = activeRowOrder
    .filter(idx => !hidden.has(Number(idx)))
    .map(idx => tableRows[idx])
    .filter(Boolean);

  return (
    <div className="karma-table-wrapper">
      <div className="karma-table-container">
        <table className="karma-table">
          <thead>
            <tr>
              {isColVisible(0) && <th colSpan="2">{t('table.path')}</th>}
              {isColVisible(1) && <th colSpan="2">{t('table.achievement')}</th>}
              {isColVisible(2) && <th colSpan="2">{t('table.karmic')}</th>}
            </tr>
          </thead>
          <tbody>
            {orderedRows.map((row) => (
              <tr key={row.id}>
                {row.cells.map((cell, ci) => {
                  if (!isColVisible(ci)) return null;
                  const dynLabel = getNodeLabel(cell.nodeId);
                  const dynLetter = getNodeLetter(cell.nodeId);
                  return (
                    <React.Fragment key={ci}>
                      <TableCell
                        label={dynLabel || cell.defaultLabel}
                        value={v(cell.mainKey)}
                        colorClass={cell.mainColor}
                      />
                      <TableCell
                        label={(dynLetter || cell.defaultLabel.split('.')[0]) + '1'}
                        value={v(cell.addKey)}
                        colorClass={cell.addColor}
                      />
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
            {extra.map((custom) => (
              <tr key={`custom-${custom.id}`} data-testid={`custom-row-${custom.id}`}>
                {(custom.cells || []).slice(0, 3).map((c, ci) => {
                  if (!isColVisible(ci)) return null;
                  const computed = evaluateFormula
                    ? evaluateFormula(c.formula, results)
                    : null;
                  return (
                    <React.Fragment key={ci}>
                      <td className="karma-td" colSpan="2">
                        <div className="cell-label">{c.label || `Xana ${ci + 1}`}</div>
                        <div className="cell-value" style={{ color: c.color || '#a855f7' }}>
                          {computed !== null && computed !== undefined ? computed : '—'}
                        </div>
                      </td>
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default KarmaTable;
